import React, { useState, useEffect, useCallback, useMemo, memo } from "react";
import { MODE_KEY_TO_ID } from "../../common/ENUMS/VENTILATOR_MODES_ENUM";
import './index.scss';
import { VENTILATOR_ADDITIONAL_FEATURES } from "../../common/ENUMS/PARAMETER_ENUM";
import SnakeCanvas from "../Chart/SnakeCanvas";
import whiteUpArrow from "../../common/icons/svg/white-up-arrow.svg";
import whiteDownArrow from "../../common/icons/svg/white-down-arrow.svg";
import { handleOutsideClick } from "../../common/utils/CommonUtils";

const AUX_DROPDOWN_ID = "ventilator-aux-dropdown-mobile";

const VentilatorChartMobileView = ({
    ventilatorPatientModeAlarmProfileData,
    ventilatorAdditionalFeaturesData,
    ventilatorModuleConnectionStatus,
    tppEnabled,
    dataRange,
    graphDrawerRef,
    canvasSize,
}) => {
    const modeId = ventilatorPatientModeAlarmProfileData?.modeId || null;

    const hasCo2 = !!ventilatorAdditionalFeaturesData?.find(
        f => f.id === VENTILATOR_ADDITIONAL_FEATURES?.capnography
    )?.on;
    const hasSpo2 = !!ventilatorModuleConnectionStatus?.spo2;
    const hasTPP = !!tppEnabled;

    const auxGraphList = useMemo(() => {
        const list = [];
        if (hasSpo2) list.push({ key: "spo2-graphs", label: "SPO2" });
        if (hasCo2) list.push({ key: "co2-graphs", label: "EtCO2" });
        if (hasTPP) list.push({ key: "tpp-graphs", label: "TPP" });
        return list;
    }, [hasSpo2, hasCo2, hasTPP]);

    const hasAuxGraph = auxGraphList.length > 0;
    const dropDownVisible = auxGraphList.length > 1;

    const [selectedAuxGraphKey, setSelectedAuxGraphKey] = useState(
        () => auxGraphList[0]?.key || null
    );
    const [auxDropdownOpen, setAuxDropdownOpen] = useState(false);

    useEffect(() => {
        if (!hasAuxGraph) return;
        const isStillAvailable = auxGraphList.some(g => g.key === selectedAuxGraphKey);
        if (!isStillAvailable) {
            setSelectedAuxGraphKey(auxGraphList[0].key);
        }
    }, [auxGraphList, hasAuxGraph, selectedAuxGraphKey]);

    const handleAuxDropdownToggle = (event) => {
        if (!auxDropdownOpen) {
            handleOutsideClick(event, AUX_DROPDOWN_ID, setAuxDropdownOpen);
        }
    };

    const handleAuxOptionSelect = (option) => {
        setSelectedAuxGraphKey(option.key);
        setAuxDropdownOpen(false);
    };

    const effectiveSelectedAuxKey =
        auxGraphList.find(g => g.key === selectedAuxGraphKey)?.key ||
        auxGraphList[0]?.key ||
        null;

    const onSpo2WindowComplete = useCallback((min, max) => {
        if (min != null && max != null) {
            const absMax = Math.abs(max);
            const maxWithMargin = Math.ceil(absMax * 1.3 / 2) * 2;
            dataRange.current.spo2 = { min: 0, max: maxWithMargin };
        }
    }, [dataRange]);

    const onSpo2DrawerChange = useCallback((drawer) => {
        graphDrawerRef.current.spo2 = drawer;
    }, [graphDrawerRef]);

    const getSpo2Range = useCallback(() => dataRange.current.spo2, [dataRange]);

    const onCo2WindowComplete = useCallback((min, max) => {
        if (min != null && max != null) {
            const absMax = Math.abs(max);
            const maxWithMargin = Math.ceil(absMax * 1.2 / 2) * 2;
            dataRange.current.co2 = { min: 0, max: maxWithMargin };
        }
    }, [dataRange]);

    const onCo2DrawerChange = useCallback((drawer) => {
        graphDrawerRef.current.co2 = drawer;
    }, [graphDrawerRef]);

    const getCo2Range = useCallback(() => dataRange.current.co2, [dataRange]);

    const onTppWindowComplete = useCallback((min, max) => {
        if (min != null && max != null) {
            const absMax = Math.abs(max);
            const maxWithMargin = Math.ceil(absMax * 1.3 / 2) * 2;
            dataRange.current.tpp = { min: -maxWithMargin, max: maxWithMargin };
        }
    }, [dataRange]);

    const onTppDrawerChange = useCallback((drawer) => {
        graphDrawerRef.current.tpp = drawer;
    }, [graphDrawerRef]);

    const getTppRange = useCallback(() => dataRange.current.tpp, [dataRange]);

    const onPressureWindowComplete = useCallback((min, max) => {
        if (min != null && max != null) {
            const absMax = Math.abs(max);
            const maxWithMargin = Math.ceil(absMax * 1.3 / 2) * 2;
            dataRange.current.pressure = { min: 0, max: maxWithMargin };
        }
    }, [dataRange]);

    const onPressureDrawerChange = useCallback((drawer) => {
        graphDrawerRef.current.pressure = drawer;
    }, [graphDrawerRef]);

    const getPressureRange = useCallback(() => dataRange.current.pressure, [dataRange]);

    const onFlowWindowComplete = useCallback((min, max) => {
        if (min != null && max != null) {
            const absMax = Math.max(Math.abs(min), Math.abs(max));
            const maxWithMargin = Math.ceil(absMax * 1.3 / 2) * 2;
            dataRange.current.flow = {
                min: -maxWithMargin,
                max: maxWithMargin,
            };
        }
    }, [dataRange]);

    const onFlowDrawerChange = useCallback((drawer) => {
        graphDrawerRef.current.flow = drawer;
    }, [graphDrawerRef]);

    const getFlowRange = useCallback(() => dataRange.current.flow, [dataRange]);

    const onVolumeWindowComplete = useCallback((min, max) => {
        if (min != null && max != null) {
            const absMax = Math.abs(max);
            const maxWithMargin = Math.ceil(absMax * 1.3 / 2) * 2;
            dataRange.current.volume = { min: 0, max: maxWithMargin };
        }
    }, [dataRange]);

    const onVolumeDrawerChange = useCallback((drawer) => {
        graphDrawerRef.current.volume = drawer;
    }, [graphDrawerRef]);

    const getVolumeRange = useCallback(() => dataRange.current.volume, [dataRange]);

    const getAxisConfigSpo2 = useCallback(() => ({
        yAxis: { labels: { show: false } },
    }), []);

    const getAxisConfigDefault = useCallback(() => ({
        yAxis: {
            labels: {
                color: "#666666",
            },
        },
        xAxis: {
            labels: {
                show: true,
                color: "#666666",
            },
        },
    }), []);

    const handleCanvasSizeChange = useCallback((width, height) => {
        canvasSize.current = { width, height };
    }, [canvasSize]);

    const auxGraphConfigs = useMemo(() => ({
        "spo2-graphs": {
            key: "spo2-graphs",
            label: "SPO2",
            unit: "(%)",
            onWindowComplete: onSpo2WindowComplete,
            onGraphDrawerChange: onSpo2DrawerChange,
            getDataRange: getSpo2Range,
            borderColor: "#EF9A46",
            stepSize: 1,
        },
        "co2-graphs": {
            key: "co2-graphs",
            label: "EtCO2",
            unit: "(mmHg)",
            onWindowComplete: onCo2WindowComplete,
            onGraphDrawerChange: onCo2DrawerChange,
            getDataRange: getCo2Range,
            borderColor: "#5247E7",
            stepSize: 1,
        },
        "tpp-graphs": {
            key: "tpp-graphs",
            label: "TPP",
            unit: "",
            onWindowComplete: onTppWindowComplete,
            onGraphDrawerChange: onTppDrawerChange,
            getDataRange: getTppRange,
            borderColor: "#fadd28",
            stepSize: 1,
            nPointsPerWindow: 10000,
        },
    }), [
        onSpo2WindowComplete,
        onSpo2DrawerChange,
        getSpo2Range,
        onCo2WindowComplete,
        onCo2DrawerChange,
        getCo2Range,
        onTppWindowComplete,
        onTppDrawerChange,
        getTppRange,
    ]);

    const { graphs, flexValue } = useMemo(() => {
        const list = [];
        let flex;

        if (modeId === MODE_KEY_TO_ID["HFOT"]) {
            list.push({
                key: "flow-graphs",
                label: "Flow",
                unit: "(l/min)",
                onWindowComplete: onFlowWindowComplete,
                onGraphDrawerChange: onFlowDrawerChange,
                getDataRange: getFlowRange,
                getAxisConfig: getAxisConfigDefault,
                borderColor: "#FCC331",
                stepSize: 2,
            });

            if (hasAuxGraph && effectiveSelectedAuxKey) {
                const aux = auxGraphConfigs[effectiveSelectedAuxKey];
                list.push({
                    ...aux,
                    isAuxSlot: true,
                    getAxisConfig: aux.key.includes("spo2") ? getAxisConfigSpo2 : getAxisConfigDefault,
                });
            }

            flex = list.length === 1 ? "0 0 86%" : "0 0 36%";
        } else {
            list.push({
                key: "pressure-graph",
                label: "Pressure",
                unit: "(cmH2O)",
                onWindowComplete: onPressureWindowComplete,
                onGraphDrawerChange: onPressureDrawerChange,
                getDataRange: getPressureRange,
                getAxisConfig: getAxisConfigDefault,
                borderColor: "#3ADAE5",
                stepSize: 1,
            });

            list.push({
                key: "flow-graphs",
                label: "Flow",
                unit: "(l/min)",
                onWindowComplete: onFlowWindowComplete,
                onGraphDrawerChange: onFlowDrawerChange,
                getDataRange: getFlowRange,
                getAxisConfig: getAxisConfigDefault,
                borderColor: "#FCC331",
                stepSize: 2,
            });

            list.push({
                key: "volume-graph",
                label: "Volume",
                unit: "(ml)",
                onWindowComplete: onVolumeWindowComplete,
                onGraphDrawerChange: onVolumeDrawerChange,
                getDataRange: getVolumeRange,
                getAxisConfig: getAxisConfigDefault,
                borderColor: "#299842",
                stepSize: 1,
            });

            if (hasAuxGraph && effectiveSelectedAuxKey) {
                const aux = auxGraphConfigs[effectiveSelectedAuxKey];
                list.push({
                    ...aux,
                    isAuxSlot: true,
                    getAxisConfig: aux.key.includes("spo2") ? getAxisConfigSpo2 : getAxisConfigDefault,
                });
            }

            flex = "0 0 21%";
        }

        return { graphs: list, flexValue: flex };
    }, [
        modeId,
        hasAuxGraph,
        effectiveSelectedAuxKey,
        auxGraphConfigs,
        onFlowWindowComplete,
        onFlowDrawerChange,
        getFlowRange,
        onPressureWindowComplete,
        onPressureDrawerChange,
        getPressureRange,
        onVolumeWindowComplete,
        onVolumeDrawerChange,
        getVolumeRange,
        getAxisConfigSpo2,
        getAxisConfigDefault,
    ]);

    const renderGraphLabel = (label) => {
        if (label === "SPO2") return (<>SPO<sub>2</sub></>);
        if (label === "EtCO2") return (<>EtCO<sub>2</sub></>);
        return label;
    };

    return (
        <>
            {graphs.map(graph => (
                <div
                    className="graph"
                    key={graph.key}
                    style={{
                        flex: flexValue,
                        margin: "4.5vw 4vw 10vw 0",
                        minHeight: "66px",
                    }}
                >
                    {graph.isAuxSlot && dropDownVisible ? (
                        <div className="ventilator-aux-header-mobile">
                            <div
                                className="ventilator-aux-select-container-mobile"
                                onClick={handleAuxDropdownToggle}
                            >
                                <span className="ventilator-aux-selected-label">
                                    {renderGraphLabel(graph.label)}
                                </span>
                                {graph.unit && (
                                    <span className="ventilator-aux-unit">{graph.unit}</span>
                                )}
                                <img
                                    className="ventilator-aux-arrow"
                                    src={auxDropdownOpen ? whiteUpArrow : whiteDownArrow}
                                    alt={auxDropdownOpen ? "up" : "down"}
                                />
                                {auxDropdownOpen && (
                                    <div
                                        className="ventilator-aux-options"
                                        id={AUX_DROPDOWN_ID}
                                    >
                                        {auxGraphList.map(option => (
                                            <div
                                                key={option.key}
                                                className={
                                                    option.key === effectiveSelectedAuxKey
                                                        ? "ventilator-aux-option ventilator-aux-option-active"
                                                        : "ventilator-aux-option"
                                                }
                                                onClick={() => handleAuxOptionSelect(option)}
                                            >
                                                {renderGraphLabel(option.label)}
                                            </div>
                                        ))}
                                    </div>
                                )}
                            </div>
                        </div>
                    ) : (
                        <div
                            style={{
                                display: "flex",
                                alignItems: "baseline",
                                gap: "4px",
                                marginLeft: "20px",
                                marginBottom: "12px",
                            }}
                        >
                            <span
                                style={{
                                    fontFamily: "Noto Sans",
                                    fontSize: "clamp(13px, 3.9vmin, 20px)",
                                    fontWeight: 600,
                                }}
                            >
                                {renderGraphLabel(graph.label)}
                            </span>

                            {graph.unit && (
                                <span
                                    style={{
                                        fontFamily: "Noto Sans",
                                        fontSize: "clamp(6px, 2.9vmin, 12px)",
                                        fontWeight: 400,
                                        color: "#B3B3B3",
                                    }}
                                >
                                    {graph.unit}
                                </span>
                            )}
                        </div>
                    )}

                    <SnakeCanvas
                        name={graph.key}
                        nPointsPerWindow={graph.nPointsPerWindow || 5000}
                        headTailGapPx={100}
                        onWindowComplete={graph.onWindowComplete}
                        onWindowSizeChange={handleCanvasSizeChange}
                        onGraphDrawerChange={graph.onGraphDrawerChange}
                        getDataRange={graph.getDataRange}
                        getAxisConfig={graph.getAxisConfig}
                    />
                </div>
            ))}
        </>
    );
};

export default memo(VentilatorChartMobileView);
